'use client'

import { useState } from 'react'
import Link from 'next/link'
import { Box, Typography } from '@mui/material'
import { HtmlPurifier } from '@components/common'
import { getStyles } from '@utils/styles'
import defaultStyle from './styles'

export type BlogsSectionData = {
  description: string
  image?: { src: string; alt: string }
}

type TocItem = { id: string; label: string }

function prepareArticle(html: string) {
  const items: TocItem[] = []
  let sectionNumber = 0
  // CMS content often contains spacer paragraphs made only from non-breaking
  // spaces or line breaks. They create large, inconsistent gaps in articles.
  const normalizedHtml = html
    .replace(/<p\b[^>]*>(?:\s|&nbsp;|&#160;|<br\s*\/?>)*<\/p>/gi, '')
    .replace(/(?:<br\s*\/?>\s*){3,}/gi, '<br />')

  const articleHtml = normalizedHtml.replace(
    /<h2\b([^>]*)>([\s\S]*?)<\/h2>/gi,
    (fullHeading, attributes: string, title: string) => {
      sectionNumber += 1
      const existingId = /\sid=["']([^"']+)["']/i.exec(attributes)?.[1]
      const id = existingId || `article-section-${sectionNumber}`
      const label = title
        .replace(/<[^>]*>/g, '')
        .replace(/&nbsp;/g, ' ')
        .trim()
      if (label) items.push({ id, label })
      if (existingId) return fullHeading
      return `<h2${attributes} id="${id}">${title}</h2>`
    }
  )
  return { articleHtml, items }
}

const testLinks = [
  ['Complete Blood Count', '/tests/complete-blood-count-cbc'],
  ['Thyroid Profile', '/book-a-test'],
  ['Kidney Function Test', '/book-a-test'],
  ['Liver Function Test', '/book-a-test'],
]

const cities = ['delhi', 'hyderabad', 'mumbai', 'chennai', 'bangalore', 'pune']

const blogCategories = [
  {
    label: 'Diseases & Conditions',
    links: [
      ['Diabetes', '/blogs/category/diabetes'],
      ['Thyroid', '/blogs/category/thyroid'],
      ['Heart Diseases', '/blogs/category/heart-diseases'],
      ['Kidney', '/blogs/category/kidney'],
    ],
  },
  {
    label: 'Tests & Diagnostics',
    links: [
      ['Diagnostics & Pathology', '/blogs/category/diagnostics-pathology'],
      ['Medical Tests', '/blogs/category/medical-tests'],
      ['Health Checkups', '/blogs/category/health-checkups'],
    ],
  },
  {
    label: 'Preventive Health',
    links: [
      ['Health Tips', '/blogs/category/health-tips'],
      ['Fitness', '/blogs/category/fitness'],
      ['Health Checkups', '/blogs/category/health-checkups'],
    ],
  },
  {
    label: 'Nutrition & Diet',
    links: [
      ['Nutrition & Recipes', '/blogs/category/nutrition-recipes'],
      ['Health Supplements', '/blogs/category/health-supplements'],
      ['Food Intolerance', '/blogs/category/food-intolerance'],
    ],
  },
  {
    label: "Women's Health",
    links: [
      ["Women's Health", '/blogs/category/womens-health'],
      ['Pregnancy', '/blogs/category/pregnancy'],
      ['Menstrual Problems', '/blogs/category/menstrual-problems'],
    ],
  },
  {
    label: "Men's Health",
    links: [["Men's Health", '/blogs/category/mens-health']],
  },
  {
    label: 'Child & Elderly Care',
    links: [
      ['Child Health', '/blogs/category/child-health'],
      ['Parent Care & Old Age', '/blogs/category/parent-care-old-age'],
    ],
  },
  {
    label: 'Skin & Allergies',
    links: [
      ['Skin & Hair Problems', '/blogs/category/skin-hair-problems'],
      ['Allergy', '/blogs/category/allergy'],
    ],
  },
  {
    label: 'Weight Management',
    links: [['Weight Management', '/blogs/category/weight-management']],
  },
]

const relatedArticles = [
  ['Latest health insights', '/blogs'],
  ['Preventive health guides', '/blogs/category/preventive-health'],
  ['Diagnostic test articles', '/blogs/category/tests-and-diagnostics'],
]

export function BlogsSection({ description, image }: BlogsSectionData) {
  const [categoriesOpen, setCategoriesOpen] = useState(true)
  const [expandedCategory, setExpandedCategory] = useState<string | null>(null)
  const styles = getStyles(defaultStyle)
  const { articleHtml, items } = prepareArticle(description)
  const hasEmbeddedTableOfContents = /table\s*(?:of|&)\s*contents/i.test(
    description.replace(/<[^>]*>/g, ' ').replace(/&nbsp;|&#160;/gi, ' ')
  )

  return (
    <Box {...styles('page')}>
      <Box {...styles('layout')}>
        <Box component="main" {...styles('main')}>
          {image && (
            <Box {...styles('featuredImageWrapper')}>
              <Box
                component="img"
                src={image.src}
                alt={image.alt}
                {...styles('featuredImage')}
              />
            </Box>
          )}
          {items.length > 0 && !hasEmbeddedTableOfContents && (
            <Box
              component="nav"
              aria-label="Table of contents"
              {...styles('toc')}
            >
              <Typography component="h2" {...styles('tocTitle')}>
                Table of Contents
              </Typography>
              <Typography {...styles('tocSubtitle')}>
                Jump directly to a section
              </Typography>
              <Box component="ol" {...styles('tocList')}>
                {items.map((item) => (
                  <Box component="li" key={item.id} {...styles('tocItem')}>
                    <Box
                      component="a"
                      href={`#${item.id}`}
                      {...styles('tocLink')}
                    >
                      {item.label}
                    </Box>
                  </Box>
                ))}
              </Box>
            </Box>
          )}

          <Box component="article" {...styles('article')}>
            <HtmlPurifier
              html={articleHtml}
              customStyles={{ wrapper: styles('articleBody').sx }}
            />
          </Box>

          <Box {...styles('related')}>
            <Box>
              <Typography component="h2" {...styles('relatedTitle')}>
                Related Health Articles
              </Typography>
              <Typography {...styles('relatedText')}>
                Explore more expert guidance on diagnostics and preventive
                health.
              </Typography>
            </Box>
            <Link href="/blogs" style={{ textDecoration: 'none' }}>
              <Box component="span" {...styles('relatedAction')}>
                View all articles
              </Box>
            </Link>
          </Box>
        </Box>

        <Box component="aside" {...styles('sidebar')}>
          <Box {...styles('categoryPanel')}>
            <Box
              component="button"
              type="button"
              aria-expanded={categoriesOpen}
              aria-controls="blog-category-links"
              onClick={() => setCategoriesOpen((open) => !open)}
              {...styles('categoryHeader')}
            >
              <Typography {...styles('categoryHeaderTitle')}>
                Blog Categories
              </Typography>
              <Box {...styles('categoryHeaderMeta')}>
                <Typography {...styles('categoryCount')}>
                  9 Categories
                </Typography>
                <Box
                  component="span"
                  {...styles(
                    categoriesOpen
                      ? ['categoryChevron', 'categoryChevronOpen']
                      : 'categoryChevron'
                  )}
                >
                  ⌄
                </Box>
              </Box>
            </Box>
            {categoriesOpen && (
              <Box id="blog-category-links" {...styles('categoryLinks')}>
                {blogCategories.map(({ label, links }) => {
                  const isOpen = expandedCategory === label
                  return (
                    <Box key={label} {...styles('categoryItem')}>
                      <Box
                        component="button"
                        type="button"
                        aria-expanded={isOpen}
                        onClick={() =>
                          setExpandedCategory(isOpen ? null : label)
                        }
                        {...styles('categoryLink')}
                      >
                        {label}
                        <Box
                          component="span"
                          {...styles(
                            isOpen
                              ? ['itemChevron', 'itemChevronOpen']
                              : 'itemChevron'
                          )}
                        >
                          ›
                        </Box>
                      </Box>
                      {isOpen && (
                        <Box {...styles('categorySubLinks')}>
                          {links.map(([subLabel, href]) => (
                            <Link
                              href={href}
                              key={subLabel}
                              style={{ textDecoration: 'none' }}
                            >
                              <Typography {...styles('categorySubLink')}>
                                {subLabel}
                              </Typography>
                            </Link>
                          ))}
                        </Box>
                      )}
                    </Box>
                  )
                })}
              </Box>
            )}
          </Box>
          <Box {...styles('sidePanel')}>
            <Typography {...styles('sideTitle')}>
              Related Health Articles
            </Typography>
            {relatedArticles.map(([label, href]) => (
              <Link href={href} key={label} style={{ textDecoration: 'none' }}>
                <Typography {...styles('relatedSideLink')}>
                  {label}
                  <Box component="span">→</Box>
                </Typography>
              </Link>
            ))}
          </Box>
          <Box {...styles('callback')}>
            <Typography {...styles('callbackLabel')}>NEED HELP?</Typography>
            <Typography component="h2" {...styles('callbackTitle')}>
              Get Instant Call Back
            </Typography>
            <Typography {...styles('callbackText')}>
              Speak with our health advisor for help choosing the right test.
            </Typography>
            <Box
              component="button"
              type="button"
              onClick={() =>
                window.dispatchEvent(new Event('apollo:open-callback-form'))
              }
              {...styles('callbackButton')}
            >
              Request Call Back
            </Box>
          </Box>
          <Box {...styles('sidePanel')}>
            <Typography {...styles('sideTitle')}>Top Tests</Typography>
            {testLinks.map(([label, href]) => (
              <Link href={href} key={label} style={{ textDecoration: 'none' }}>
                <Typography {...styles('sideLink')}>{label}</Typography>
              </Link>
            ))}
          </Box>
          <Box {...styles('sidePanel')}>
            <Typography {...styles('sideTitle')}>
              Health Check Packages
            </Typography>
            <Link
              href="/health-check-packages"
              style={{ textDecoration: 'none' }}
            >
              <Typography {...styles('sideLink')}>
                View health packages
              </Typography>
            </Link>
            <Link href="/mens-health" style={{ textDecoration: 'none' }}>
              <Typography {...styles('sideLink')}>
                Men&apos;s health packages
              </Typography>
            </Link>
            <Link href="/womens-health" style={{ textDecoration: 'none' }}>
              <Typography {...styles('sideLink')}>
                Women&apos;s health packages
              </Typography>
            </Link>
          </Box>
          <Box {...styles('sidePanel')}>
            <Typography {...styles('sideTitle')}>Diagnostic Centres</Typography>
            {cities.map((city) => (
              <Link
                href={`/find-nearest-centre/${city}`}
                key={city}
                style={{ textDecoration: 'none' }}
              >
                <Typography {...styles('sideLink')}>
                  Centres in {city.charAt(0).toUpperCase() + city.slice(1)}
                </Typography>
              </Link>
            ))}
          </Box>
        </Box>
      </Box>
    </Box>
  )
}
